import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Building2, Mail, User, Briefcase, MessageSquare } from 'lucide-react';

const ease = [0.16, 1, 0.3, 1];

const COMMODITIES = ['Crude', 'Refined products', 'LNG', 'Petrochemicals', 'Other'];
const TRMS = ['Allegro', 'Endur', 'RightAngle', 'Eka', 'Aspect', 'SAP CTRM', 'In-house', 'Other'];

const Field = ({ label, icon: Icon, children, required }) => (
  <label className="block">
    <span className="flex items-center gap-1.5 text-[11.5px] font-medium tracking-[0.06em] uppercase text-ink-faint mb-2.5">
      {Icon && <Icon size={11} strokeWidth={2} className="text-amber/80" />}
      {label}
      {required && <span className="text-amber">*</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  'w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-4 py-3 text-[14.5px] text-canvas-white placeholder:text-ink-faint/60 focus:outline-none focus:border-amber/50 focus:bg-white/[0.05] focus:ring-2 focus:ring-amber/15 transition-all';

const RequestDemoPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [botField, setBotField] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    commodity: '',
    trm: '',
    notes: '',
  });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Netlify Forms: urlencoded POST to any page on the site, with the form name
  // matching the hidden static form in public/index.html.
  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const body = new URLSearchParams({
        'form-name': 'demo-request',
        'bot-field': botField,
        ...form,
      }).toString();

      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setSubmitted(true);
    } catch (err) {
      setError(
        "Something went wrong sending that. Please email us at hello@coretrade.ai and we'll pick it up from there."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="relative pt-36 pb-28 md:pt-44 md:pb-32 overflow-hidden">
      <div className="absolute inset-0 bg-radial-amber pointer-events-none" />
      <div className="absolute inset-0 grid-bg pointer-events-none" />

      <div className="container-prose relative">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left: copy */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease }}
            >
              <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light mb-5">
                Pre-launch · Booking demos
              </p>
              <h1 className="text-[40px] md:text-[52px] font-bold leading-[1.05] tracking-tight text-canvas-white text-balance">
                Book a Core Trade demo.
              </h1>
              <p className="mt-6 text-[16px] leading-relaxed text-ink-faint max-w-md">
                Tell us a bit about your desk and we'll come back within one business
                day to schedule a 30-minute call. We'll walk through the product live —
                and if you bring redacted samples, we'll show it running on your own
                confirmations.
              </p>

              <ul className="mt-10 space-y-4">
                {[
                  'Founder-led demo — no pre-sales hand-off',
                  'See it run on a redacted sample of your own threads',
                  'Early-customer pricing, locked for the first 12 months',
                  "No pitch, no commitment — just see if it's a fit",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[14px] text-ink-faint">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-amber/15 ring-1 ring-amber/25 flex items-center justify-center flex-shrink-0">
                      <Check size={11} strokeWidth={3} className="text-amber" />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease }}
              className="relative p-8 md:p-10 rounded-3xl border border-white/[0.08] bg-canvas-surface/60 backdrop-blur-xl"
            >
              <div className="absolute -top-px left-10 right-10 h-px bg-gradient-to-r from-transparent via-amber/40 to-transparent" />

              {!submitted ? (
                <form
                  onSubmit={onSubmit}
                  name="demo-request"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  className="space-y-6"
                >
                  <input type="hidden" name="form-name" value="demo-request" />
                  <p className="hidden">
                    <label>
                      Leave this field empty
                      <input
                        name="bot-field"
                        tabIndex={-1}
                        autoComplete="off"
                        value={botField}
                        onChange={(e) => setBotField(e.target.value)}
                      />
                    </label>
                  </p>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Full name" icon={User} required>
                      <input
                        required
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={(e) => update('name', e.target.value)}
                        placeholder="Khalid Al-Mansoor"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Work email" icon={Mail} required>
                      <input
                        required
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        placeholder="khalid@desk.com"
                        className={inputCls}
                      />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Company" icon={Building2} required>
                      <input
                        required
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={(e) => update('company', e.target.value)}
                        placeholder="Gulf Energy Trading"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Your role" icon={Briefcase}>
                      <input
                        type="text"
                        name="role"
                        value={form.role}
                        onChange={(e) => update('role', e.target.value)}
                        placeholder="Head of Trading"
                        className={inputCls}
                      />
                    </Field>
                  </div>

                  <Field label="Primary commodity">
                    <div className="flex flex-wrap gap-2">
                      {COMMODITIES.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => update('commodity', c)}
                          className={`px-3.5 py-1.5 rounded-full text-[12.5px] transition-all ring-1 ${
                            form.commodity === c
                              ? 'bg-amber/15 text-amber-light ring-amber/40'
                              : 'bg-white/[0.03] text-ink-faint ring-white/[0.08] hover:ring-white/[0.15]'
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label="TRM / booking system">
                    <div className="flex flex-wrap gap-2">
                      {TRMS.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => update('trm', t)}
                          className={`px-3.5 py-1.5 rounded-full text-[12.5px] transition-all ring-1 ${
                            form.trm === t
                              ? 'bg-amber/15 text-amber-light ring-amber/40'
                              : 'bg-white/[0.03] text-ink-faint ring-white/[0.08] hover:ring-white/[0.15]'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label="Anything else we should know?" icon={MessageSquare}>
                    <textarea
                      rows={3}
                      name="notes"
                      value={form.notes}
                      onChange={(e) => update('notes', e.target.value)}
                      placeholder="Volume per day, pain points, anything specific to your desk..."
                      className={inputCls}
                    />
                  </Field>

                  <div className="pt-2 flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-[11.5px] text-ink-faint/80 max-w-xs">
                      We'll only use this to schedule your walkthrough. No marketing list, ever.
                    </p>
                    <button
                      type="submit"
                      disabled={sending}
                      className="group inline-flex items-center gap-1.5 px-6 py-3 rounded-full bg-amber text-canvas-black text-[14.5px] font-semibold hover:bg-amber-light shadow-[0_8px_24px_-8px_rgba(204,132,0,0.55)] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {sending ? 'Sending…' : 'Book my demo'}
                      <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  </div>

                  {error && (
                    <p className="text-[12.5px] leading-relaxed text-red-300/90 bg-red-500/10 ring-1 ring-red-500/20 rounded-xl px-4 py-3">
                      {error}
                    </p>
                  )}
                </form>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease }}
                  className="text-center py-10"
                >
                  <div className="mx-auto w-14 h-14 rounded-full bg-amber/15 ring-1 ring-amber/30 flex items-center justify-center mb-6">
                    <Check size={22} strokeWidth={2.5} className="text-amber" />
                  </div>
                  <h3 className="text-[26px] font-bold tracking-tight text-canvas-white mb-3">
                    We're on it.
                  </h3>
                  <p className="text-[14.5px] leading-relaxed text-ink-faint max-w-md mx-auto">
                    Thanks{form.name ? `, ${form.name.split(' ')[0]}` : ''}. One of the founders
                    will be in touch within one business day to schedule your demo.
                  </p>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RequestDemoPage;
